import { Component } from '@theme/component';

/**
 * A custom element that renders a ladder of quantity tiers on the product page.
 *
 * Each tier is a radio input whose value is a quantity. Selecting a tier pushes
 * that quantity into Horizon's `quantity-selector-component` in the same
 * section (the one rendered inside the buy-buttons product form), so the add
 * to cart form submits the tier's quantity. Pricing on the tiers is
 * display-only — discounts are enforced at checkout by the store's discount
 * rules.
 *
 * @extends Component<{}>
 */
class ShtfQuantityTiers extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Sync the product form with the preselected tier (which may not be qty 1).
    const checked = this.querySelector('input[type="radio"]:checked');

    if (checked instanceof HTMLInputElement) {
      // Wait for the quantity selector to be defined and connected.
      customElements.whenDefined('quantity-selector-component').then(() => {
        requestAnimationFrame(() => this.#applyQuantity(checked.value));
      });
    }
  }

  /**
   * Handles a tier radio being selected.
   * @param {Event} event - The change event from the radio input.
   */
  selectTier(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    for (const row of this.querySelectorAll('.shtf-tiers__row')) {
      row.classList.toggle('shtf-tiers__row--selected', row.contains(input));
    }

    this.#applyQuantity(input.value);
  }

  /**
   * Pushes a quantity into the section's quantity selector / product form.
   * @param {string} value - The quantity to set.
   */
  #applyQuantity(value) {
    const quantity = Number.parseInt(value, 10);
    if (!Number.isFinite(quantity) || quantity < 1) return;

    const section = this.closest('.shopify-section') ?? document;
    const selector = section.querySelector('quantity-selector-component');

    if (selector && typeof (/** @type {any} */ (selector).setValue) === 'function') {
      const component = /** @type {any} */ (selector);
      component.setValue(String(quantity));
      component.onQuantityChange?.();
      component.updateButtonStates?.();
      return;
    }

    // Fallback: set the raw form input directly.
    const input = section.querySelector(
      'form[data-type="add-to-cart-form"] input[name="quantity"], input[name="quantity"]'
    );

    if (input instanceof HTMLInputElement) {
      input.value = String(quantity);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

if (!customElements.get('shtf-quantity-tiers')) {
  customElements.define('shtf-quantity-tiers', ShtfQuantityTiers);
}
